import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { 
  ArrowLeftRight, 
  Plus, 
  Check, 
  X, 
  Clock, 
  User,
  Calendar,
  MapPin,
  Filter,
  ChevronDown,
  Trash2
} from 'lucide-react';
import { shiftSwapsAPI, jobsAPI, employeesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export default function ShiftSwaps() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [swaps, setSwaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Create swap dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [myJobs, setMyJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [swapReason, setSwapReason] = useState('');
  const [creating, setCreating] = useState(false);
  
  // For admin: select which employee is requesting
  const [requestingEmployee, setRequestingEmployee] = useState('');

  const fetchSwaps = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const res = await shiftSwapsAPI.getAll(params);
      setSwaps(res.data.swaps || []);
    } catch (error) {
      toast.error('Failed to load shift swaps');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchMyJobs = useCallback(async () => {
    try {
      const res = await shiftSwapsAPI.getMyJobs();
      setMyJobs(res.data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
  }, []);

  const fetchAllJobs = useCallback(async () => {
    try {
      const res = await jobsAPI.getAll();
      // Filter to jobs with assigned employees
      const jobs = (res.data.jobs || res.data || []).filter(j => 
        j.assigned_to && j.assigned_to.length > 0 &&
        j.status !== 'completed' && j.status !== 'cancelled'
      );
      setAllJobs(jobs);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
  }, []);

  const fetchAllEmployees = useCallback(async () => {
    try {
      const res = await employeesAPI.getAll({ active_only: true });
      setEmployees(res.data || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  }, []);

  useEffect(() => {
    fetchSwaps();
    if (isAdmin) {
      fetchAllJobs();
      fetchAllEmployees();
    } else {
      fetchMyJobs();
    }
  }, [fetchSwaps, fetchAllJobs, fetchAllEmployees, fetchMyJobs, isAdmin]);

  const fetchAvailableEmployees = async (jobId) => {
    try {
      const res = await shiftSwapsAPI.getAvailableEmployees(jobId);
      setAvailableEmployees(res.data.employees || []);
    } catch (error) {
      console.error('Failed to fetch available employees:', error);
    }
  };

  const handleJobSelect = (jobId) => {
    setSelectedJob(jobId);
    setSelectedEmployee('');
    if (jobId) {
      fetchAvailableEmployees(jobId);
    } else {
      setAvailableEmployees([]);
    }
  };

  const handleCreateSwap = async () => {
    if (!selectedJob || !selectedEmployee) {
      toast.error('Please select a job and target employee');
      return;
    }

    setCreating(true);
    try {
      await shiftSwapsAPI.create({
        job_id: selectedJob,
        target_employee_id: selectedEmployee,
        reason: swapReason || null
      });
      toast.success('Shift swap request created');
      setShowCreateDialog(false);
      resetForm();
      fetchSwaps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create swap request');
    } finally {
      setCreating(false);
    }
  };

  const handleApprove = async (swapId) => {
    try {
      await shiftSwapsAPI.approve(swapId);
      toast.success('Shift swap approved and executed');
      fetchSwaps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve swap');
    }
  };

  const handleReject = async (swapId) => {
    try {
      await shiftSwapsAPI.reject(swapId);
      toast.success('Shift swap rejected');
      fetchSwaps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject swap');
    }
  };

  const handleCancel = async (swapId) => {
    try {
      await shiftSwapsAPI.cancel(swapId);
      toast.success('Shift swap request cancelled');
      fetchSwaps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel swap');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Delete ALL shift swap records? This permanently removes every request and cannot be undone.')) return;
    try {
      const res = await shiftSwapsAPI.clearAll();
      toast.success(res.data.message);
      fetchSwaps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear records');
    }
  };

  const handleDeletePermanently = async (swapId) => {
    if (!window.confirm('Permanently delete this shift swap record? This cannot be undone.')) return;
    try {
      await shiftSwapsAPI.deletePermanently(swapId);
      toast.success('Shift swap record deleted');
      fetchSwaps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete swap record');
    }
  };

  const resetForm = () => {
    setSelectedJob('');
    setSelectedEmployee('');
    setSwapReason('');
    setRequestingEmployee('');
    setAvailableEmployees([]);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
    };
    return (
      <Badge className={styles[status] || styles.pending}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const jobsForSelect = isAdmin ? allJobs : myJobs;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ArrowLeftRight className="w-6 h-6 text-primary" />
            Shift Swaps
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isAdmin ? 'Manage employee shift swap requests' : 'Request to swap your shifts with other employees'}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && swaps.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleClearAll}
              data-testid="clear-all-swaps-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear All
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                {statusFilter === 'all' ? 'All Status' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setStatusFilter('all')}>All Status</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('pending')}>Pending</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('approved')}>Approved</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('rejected')}>Rejected</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('cancelled')}>Cancelled</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-swap-btn">
            <Plus className="w-4 h-4 mr-2" />
            {isAdmin ? 'Create Swap' : 'Request Swap'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold">{swaps.filter(s => s.status === 'pending').length}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{swaps.filter(s => s.status === 'approved').length}</p>
                <p className="text-xs text-muted-foreground">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <X className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-2xl font-bold">{swaps.filter(s => s.status === 'rejected').length}</p>
                <p className="text-xs text-muted-foreground">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-primary" />
              <div>
                <p className="text-2xl font-bold">{swaps.length}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Swaps List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Swap Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : swaps.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ArrowLeftRight className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No shift swap requests found</p>
              <p className="text-sm mt-1">
                {isAdmin ? 'Employees can request shift swaps here' : 'Click "Request Swap" to get started'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {swaps.map((swap) => (
                <div 
                  key={swap.id} 
                  className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                  data-testid={`swap-${swap.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Swap Details */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold">{swap.job_title || 'Unknown Job'}</h3>
                        {getStatusBadge(swap.status)}
                        {swap.initiated_by_admin && (
                          <Badge variant="outline" className="text-xs">Admin Initiated</Badge>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {formatDateTime(swap.job_start_time)}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <div className="flex items-center gap-1 bg-muted px-2 py-1 rounded">
                          <User className="w-4 h-4" />
                          <span>{swap.requester_name}</span>
                        </div>
                        <ArrowLeftRight className="w-4 h-4 text-muted-foreground" />
                        <div className="flex items-center gap-1 bg-muted px-2 py-1 rounded">
                          <User className="w-4 h-4" />
                          <span>{swap.target_employee_name}</span>
                        </div>
                      </div>
                      
                      {swap.reason && (
                        <p className="text-sm text-muted-foreground italic">
                          Reason: {swap.reason}
                        </p>
                      )}
                      
                      <p className="text-xs text-muted-foreground">
                        Requested {formatDateTime(swap.created_at)}
                      </p>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      {swap.status === 'pending' && isAdmin && (
                        <>
                          <Button 
                            size="sm" 
                            onClick={() => handleApprove(swap.id)}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`approve-${swap.id}`}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleReject(swap.id)}
                            data-testid={`reject-${swap.id}`}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      {swap.status === 'pending' && (swap.requester_id === user?.id || isAdmin) && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleCancel(swap.id)}
                          data-testid={`cancel-${swap.id}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                      {isAdmin && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeletePermanently(swap.id)}
                          title="Permanently delete this record"
                          data-testid={`delete-swap-${swap.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Swap Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5" />
              {isAdmin ? 'Create Shift Swap' : 'Request Shift Swap'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Job Selection */}
            <div className="space-y-2">
              <Label>Select Shift/Job *</Label>
              <Select value={selectedJob} onValueChange={handleJobSelect}>
                <SelectTrigger data-testid="select-job">
                  <SelectValue placeholder="Choose a job to swap" />
                </SelectTrigger>
                <SelectContent>
                  {jobsForSelect.length === 0 ? (
                    <SelectItem value="none" disabled>
                      {isAdmin ? 'No jobs with assigned employees' : 'No upcoming shifts available'}
                    </SelectItem>
                  ) : (
                    jobsForSelect.map((job) => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.title} - {formatDateTime(job.start_time)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Target Employee Selection */}
            <div className="space-y-2">
              <Label>Swap With Employee *</Label>
              <Select 
                value={selectedEmployee} 
                onValueChange={setSelectedEmployee}
                disabled={!selectedJob}
              >
                <SelectTrigger data-testid="select-employee">
                  <SelectValue placeholder={selectedJob ? "Choose an employee" : "Select a job first"} />
                </SelectTrigger>
                <SelectContent>
                  {availableEmployees.length === 0 ? (
                    <SelectItem value="none" disabled>No available employees</SelectItem>
                  ) : (
                    availableEmployees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {selectedJob && availableEmployees.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No other employees available for this job
                </p>
              )}
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label>Reason (Optional)</Label>
              <Textarea
                placeholder="Why do you need this swap?"
                value={swapReason}
                onChange={(e) => setSwapReason(e.target.value)}
                rows={3}
                data-testid="swap-reason"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateSwap} 
              disabled={creating || !selectedJob || !selectedEmployee}
              data-testid="submit-swap"
            >
              {creating ? 'Creating...' : (isAdmin ? 'Create Swap' : 'Request Swap')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
